import React, {
  ReactElement,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Path as BreadcrumbPath } from 'src/components/molecules/BreadcrumbsPipe'
import useAppTitle from 'src/hooks/useAppTitle'
import {
  useFilteredCalculationItems,
  useFilteredCalculationDSLs,
} from 'src/hooks/useFilteredCalculationData'
import Path, { TenantPathParams } from 'src/routes/path'
import {
  getCalculationDSLs,
  selectCalculationDSLsRequestStateByParams,
} from 'src/slices/calculationDSLs/calculationDSLsSlice'
import {
  getCalculationGroup,
  selectCalculationGroupStateByParams,
} from 'src/slices/calculationGroups/calculationGroupsSlice'
import {
  clearEditCalculation,
  EditOCRFormat as EditOCRFormatPayload,
  putEditOCRFormats,
} from 'src/slices/editCalculations/editCalculationsSlice'
import {
  getStore,
  selectStoresRequestStateByParams,
  selectStoreByCode,
} from 'src/slices/stores/storesSlice'
import {
  getTenant,
  selectTenantStateByCode,
  selectTenantByCode,
} from 'src/slices/tenants/tenantsSlice'
import { useAppDispatch, useAppSelector } from 'src/store'
import EditOCRFormatTemplate from '../templates'
import Presenter from './presenter'

const EditOCRFormat: React.FC = (): ReactElement => {
  useAppTitle('利用設定 ステップ2')
  const { orgCode, storeCode, tenantCode } =
    useParams<TenantPathParams>() as TenantPathParams
  const storeParams = useMemo(
    () => ({ orgCode, storeCode }),
    [orgCode, storeCode]
  )
  const tenantParams = useMemo(
    () => ({ orgCode, storeCode, tenantCode }),
    [orgCode, storeCode, tenantCode]
  )
  const navigate = useNavigate()
  const editReadItems = useAppSelector(
    (state) => state.forms.editCalculations.editReadItems
  )
  const editOCRFormats = useAppSelector(
    (state) => state.forms.editCalculations.editOCRFormats
  )
  // 三井不動産（orgCode: 007）では非表示項目をフィルタリング
  const dsls = useFilteredCalculationDSLs(tenantParams)
  const dispatch = useAppDispatch()

  const store = useAppSelector(selectStoreByCode(storeParams))
  const tenant = useAppSelector(selectTenantByCode(tenantParams))
  // 三井不動産（orgCode: 007）では非表示項目をフィルタリング
  const calculationItems = useFilteredCalculationItems(storeParams)
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbPath[]>([])
  useEffect(() => {
    setBreadcrumbs(Presenter.breadcrumbs(store?.name, tenant?.name))
  }, [store?.name, tenant?.name])

  const tenantRequestState = useAppSelector(
    selectTenantStateByCode({ orgCode, storeCode, tenantCode })
  )
  const storeRequestState = useAppSelector(
    selectStoresRequestStateByParams({ orgCode, storeCode })
  )
  const calculationGroupRequestState = useAppSelector(
    selectCalculationGroupStateByParams({ orgCode, storeCode })
  )

  const { status } = useAppSelector(
    selectCalculationDSLsRequestStateByParams({
      orgCode,
      storeCode,
      tenantCode,
    })
  )

  // API Request
  useEffect(() => {
    if (status === 'idle') {
      dispatch(getCalculationDSLs({ orgCode, storeCode, tenantCode }))
    }
  }, [status, dispatch, orgCode, storeCode, tenantCode])

  useEffect(() => {
    if (tenantRequestState.status === 'idle') {
      dispatch(getTenant({ orgCode, storeCode, tenantCode }))
    }
  }, [dispatch, orgCode, storeCode, tenantCode, tenantRequestState.status])
  useEffect(() => {
    if (store === undefined && storeRequestState.status === 'idle') {
      dispatch(getStore({ orgCode, storeCode }))
    }
  }, [dispatch, orgCode, store, storeCode, storeRequestState.status])
  useEffect(() => {
    if (calculationGroupRequestState.status === 'idle') {
      dispatch(getCalculationGroup({ orgCode, storeCode }))
    }
  }, [dispatch, orgCode, storeCode, calculationGroupRequestState.status])

  const handleClickGoBack = (ocrFormats: EditOCRFormatPayload[]) => {
    dispatch(putEditOCRFormats(ocrFormats))
    navigate(Path.editReadItem(orgCode, storeCode, tenantCode))
  }

  const handleClickGoForward = (ocrFormats: EditOCRFormatPayload[]) => {
    dispatch(putEditOCRFormats(ocrFormats))
    navigate(Path.editCalculationDSL(orgCode, storeCode, tenantCode))
  }

  const handleClickConfirmClose = () => {
    dispatch(clearEditCalculation())
    navigate(Path.tenantCalculationTab(orgCode, storeCode, tenantCode))
  }

  const handleUpdateEditOCRFormats = useCallback(
    (restoredEditOCRFormats: EditOCRFormatPayload[]) => {
      dispatch(putEditOCRFormats(restoredEditOCRFormats))
    },
    [dispatch]
  )

  return (
    <EditOCRFormatTemplate
      editReadItems={editReadItems}
      editOCRFormats={editOCRFormats}
      calculationItems={calculationItems}
      dsls={dsls}
      breadcrumbs={breadcrumbs}
      onClickGoForward={handleClickGoForward}
      onClickGoBack={handleClickGoBack}
      onClickConfirmClose={handleClickConfirmClose}
      onUpdateEditOCRFormats={handleUpdateEditOCRFormats}
    />
  )
}

export default EditOCRFormat
